import { action, computed, observable } from 'mobx'
import { serializable, identifier, map, list, object } from 'serializr'
import { v4 } from 'uuid'
import HitList from '../../api/HitList'

class Species {
  @serializable(identifier())
  id

  @serializable
  genus

  @serializable
  species

  @serializable
  abbreviation
}

class SpeciesFilter {
  @serializable(identifier())
  _id = v4()

  @serializable(map(object(Species)))
  @observable
  speciesById = observable.map()

  @serializable(list())
  @observable
  selected = []

  @serializable(list())
  @observable
  expanded = []

  @serializable(map())
  @observable
  counts = observable.map()

  @observable
  isLoading = false

  @observable
  hasLoaded = false

  request = 0

  @action
  calculateCounts(ids = [...this.speciesById.keys()]) {
    if (ids.length > 0) this.counts.clear()

    for (const id of ids) {
      const species = this.speciesById.get(id)
      const abbreviation = SpeciesFilter.mapSpeciesToOptions(species)
      if (this.counts.has(abbreviation)) {
        this.counts.set(abbreviation, this.counts.get(abbreviation) + 1)
      } else {
        this.counts.set(abbreviation, 1)
      }
    }
  }

  getCount(abbreviation) {
    let count = 0
    if (abbreviation === 'dros') {
      for (const dros of [
        'dsim',
        'dsec',
        'dyak',
        'dere',
        'dana',
        'dpse',
        'dper',
        'dwil',
        'dmoj',
        'dvir',
        'dgri',
        'other_dros',
      ]) {
        count += this.getCount(dros)
      }
    }
    return this.counts.get(abbreviation) || count
  }

  @computed
  get getAllCounts() {
    let allCounts = this.counts.toJSON()
    allCounts.dros = this.getCount('dros')
    return allCounts
  }

  /**
   *
   * @param ids
   * @param entries - An array of species objects that has genus, species,
   *                  and id attributes.
   */
  @action
  setSpeciesById({ results = [] }) {
    for (const result of results) {
      this.speciesById.set(result.id, result)
    }
  }
  @action
  setSelected = (selected = []) => this.selected.replace(selected)
  @action
  setExpanded = (expanded = []) => this.expanded.replace(expanded)

  @action
  loadSpecies(ids, success = (json) => json, fail = (err) => console.log(err)) {
    const request = ++this.request
    const hitListApi = new HitList({ url: '/api/species' })
    this.hasLoaded = false
    this.isLoading = true
    return hitListApi
      .fetchSpecies(ids)
      .then(
        action('speciesSuccess', (json) => {
          if (request !== this.request) return
          this.speciesById.clear()
          this.setSpeciesById({ results: json.resultset.result })
          this.calculateCounts(ids)
          this.isLoading = false
          this.hasLoaded = true
          return json
        })
      )
      .then(action('cbSuccess', (json) => {
        if (request === this.request) return success(json)
      }))
      .catch(action('speciesError', (err) => {
        if (request !== this.request) return
        this.isLoading = false
        this.hasLoaded = true
        return fail(err)
      }))
  }

  /**
   * Given a species abbreviation, this function maps it to one of the
   * available species filter options.
   *
   * @param species
   * @returns
   */
  static mapSpeciesToOptions(org = {}) {
    let speciesOption
    /*
      Assume 'Dmel' if the organism is not set.
     */
    const { genus = 'Drosophila', abbreviation = 'Dmel' } = org

    if (genus.toLowerCase() === 'drosophila') {
      switch (abbreviation.toLowerCase()) {
        case 'dmel':
        case 'dsim':
        case 'dsec':
        case 'dyak':
        case 'dere':
        case 'dana':
        case 'dpse':
        case 'dper':
        case 'dwil':
        case 'dmoj':
        case 'dvir':
        case 'dgri':
          speciesOption = abbreviation.toLowerCase()
          break
        default:
          speciesOption = 'other_dros'
          break
      }
    } else {
      switch (abbreviation.toLowerCase()) {
        case 'hsap':
          speciesOption = abbreviation.toLowerCase()
          break
        // Map Synthetic constructs and objects without species to Dmel.
        case 'ssss':
          speciesOption = 'dmel'
          break
        default:
          speciesOption = 'other'
          break
      }
    }
    return speciesOption
  }

  static filterIdsBySpecies(ids = [], opts = {}) {
    const { speciesWanted = [], speciesFilter } = opts

    /*
      Return an array that contains only the species requested.
    */
    return ids.filter((id) => {
      if (speciesFilter) {
        const org = speciesFilter.speciesById.get(id)
        const speciesOption = SpeciesFilter.mapSpeciesToOptions(org)

        /*
          Check if the speciesWanted array is empty and return true if it is.
          If it isn't, return a boolean indicating if the current organism
          is in the desired list of species.
         */
        return speciesWanted.length > 0
          ? speciesWanted.findIndex(
              (species) => species.toLowerCase() === speciesOption
            ) >= 0
          : true
      }

      return true
    })
  }
}

export default SpeciesFilter
